// admin.onlytrade.ai/cert-schemas — admin schema editor.
//
// Stage A.6 / production polish (2026-05-02). Lets master_admin edit the
// per-cert JSON schema files used by the engineer-facing form (voice fill +
// validation + compliance) and consumed by the future React Native client.
//
// The editor offers two modes:
//   1. Structured — per-section card with editable fields (label, voice_prompt,
//      required, options). Safer; harder to typo; covers ~95% of edits.
//   2. JSON — raw JSON textarea for power-user edits (adding new fields,
//      reorganising sections). Validates client-side before allowing Save.
//
// Save flow:
//   - Client validates JSON parses + has the required top-level shape.
//   - PUT /api/admin/schemas/certificates/:id (admin-gated on the server).
//   - Server validates structurally + duplicate-field-id check + atomic write
//     to /opt/onlytrade/schemas/<id>.schema.json with backup to _history/.
//   - Toast on success; voice fill on the engineer form picks up fresh schema
//     within seconds (no service restart needed — public route reads from disk).

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useToast } from '../lib/toast';

type SchemaSummary = {
  id: string;
  label: string;
  category: string | null;
  trade: string[];
  version: string;
  sections_count: number;
  fields_count: number;
  editable: boolean;
  url: string | null;
};

type SchemaField = {
  id: string;
  label?: string;
  type: string;
  required?: boolean;
  voice_prompt?: string | null;
  options?: string[];
  unit?: string;
  default?: unknown;
  validation?: Record<string, unknown>;
  customer_visible?: boolean;
  [key: string]: unknown;
};

type SchemaSection = {
  id: string;
  label: string;
  letter?: string;
  help?: string;
  conditional?: Record<string, unknown> | null;
  type?: string;
  fields?: SchemaField[];
  fields_per_appliance?: SchemaField[];
  [key: string]: unknown;
};

type CertSchema = {
  id: string;
  version: string;
  label: string;
  category?: string;
  trade?: string[];
  description?: string;
  sections: SchemaSection[];
  triggers?: unknown[];
  compliance_rules?: unknown[];
  [key: string]: unknown;
};

// ── List page ───────────────────────────────────────────────────────
export default function CertSchemasListPage() {
  const nav = useNavigate();
  const [items, setItems] = useState<SchemaSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [nId, setNId] = useState('');
  const [nLabel, setNLabel] = useState('');
  const [nClone, setNClone] = useState('');   // '' = blank skeleton
  const [nErr, setNErr] = useState<string | null>(null);
  const [nBusy, setNBusy] = useState(false);

  useEffect(() => {
    api<{ schemas: SchemaSummary[] }>('/admin/schemas/certificates')
      .then(d => setItems(d.schemas))
      .catch((e: Error) => setError(e.message));
  }, []);

  async function doCreate() {
    const id = nId.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_').replace(/^_+|_+$/g, '');
    if (!id) { setNErr('Enter a form id'); return; }
    if (!nLabel.trim()) { setNErr('Enter a label'); return; }
    setNBusy(true);
    setNErr(null);
    try {
      const body: { id: string; label: string; clone_from?: string } = { id, label: nLabel.trim() };
      if (nClone) body.clone_from = nClone;
      await api('/admin/schemas/certificates', { method: 'POST', body: JSON.stringify(body) });
      nav('/cert-schemas/' + id);
    } catch (e) {
      setNErr((e as Error).message);
      setNBusy(false);
    }
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6 flex items-start justify-between gap-6">
        <div>
          <h1 className="text-2xl font-bold mb-1">Cert Schemas</h1>
          <p className="text-ink-muted text-sm max-w-2xl">
            Edit the JSON schemas that drive each cert type's engineer-facing form, voice fill prompts,
            validation rules, and customer-visibility flags. Changes propagate live within seconds — every
            save is backed up to the schema's <code>_history/</code> folder so you can roll back if needed.
          </p>
        </div>
        <button onClick={() => { setCreating(true); setNId(''); setNLabel(''); setNClone(''); setNErr(null); setNBusy(false); }}
                className="shrink-0 bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded font-semibold text-sm">
          + New Form
        </button>
      </div>

      {creating && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
             onClick={() => { if (!nBusy) setCreating(false); }}>
          <div className="bg-ink-card border border-ink-border rounded-lg p-6 w-full max-w-md"
               onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-bold mb-1">Create a new form</h2>
            <p className="text-ink-muted text-xs mb-4">
              A new form is a cert schema. Once created it appears here to edit, and self-registers as a
              cert type engineers can pick — no code change needed.
            </p>
            <label className="block text-xs font-semibold text-ink-muted uppercase tracking-wide mb-1">Form id</label>
            <input type="text" value={nId} placeholder="e.g. lpg_safety_check"
                   onChange={e => setNId(e.target.value)}
                   className="w-full bg-ink-bg border border-ink-border rounded px-3 py-2 text-sm mb-1 focus:border-brand-600 outline-none font-mono" />
            <div className="text-[11px] text-ink-muted mb-3">Lower-case letters, digits and underscores. Can&apos;t be changed later.</div>
            <label className="block text-xs font-semibold text-ink-muted uppercase tracking-wide mb-1">Label</label>
            <input type="text" value={nLabel} placeholder="e.g. LPG Safety Check"
                   onChange={e => setNLabel(e.target.value)}
                   className="w-full bg-ink-bg border border-ink-border rounded px-3 py-2 text-sm mb-3 focus:border-brand-600 outline-none" />
            <label className="block text-xs font-semibold text-ink-muted uppercase tracking-wide mb-1">Start from</label>
            <select value={nClone} onChange={e => setNClone(e.target.value)}
                    className="w-full bg-ink-bg border border-ink-border rounded px-3 py-2 text-sm mb-4 focus:border-brand-600 outline-none">
              <option value="">Blank form (one section, one field)</option>
              {(items || []).map(s => (
                <option key={s.id} value={s.id}>Clone of: {s.label}</option>
              ))}
            </select>
            {nErr && <div className="bg-red-900/30 border border-red-700 text-red-200 p-2 rounded mb-3 text-xs">{nErr}</div>}
            <div className="flex gap-2 justify-end">
              <button onClick={() => setCreating(false)} disabled={nBusy}
                      className="px-4 py-2 rounded text-sm text-ink-muted hover:text-ink disabled:opacity-50">Cancel</button>
              <button onClick={doCreate} disabled={nBusy}
                      className="bg-brand-600 hover:bg-brand-700 disabled:opacity-60 text-white px-5 py-2 rounded font-semibold text-sm">
                {nBusy ? 'Creating…' : 'Create & edit'}
              </button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-900/30 border border-red-700 text-red-200 p-3 rounded mb-4">{error}</div>
      )}

      {items === null ? (
        <div className="text-ink-muted">Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-ink-card border border-ink-border rounded-lg p-8 text-center">
          <div className="text-3xl mb-2">📋</div>
          <div className="font-semibold mb-1">No schemas yet</div>
          <div className="text-ink-muted text-sm">
            Schemas live as JSON files under <code>/opt/onlytrade/schemas/</code>. Add a new file there to
            register a new cert type.
          </div>
        </div>
      ) : (
        <div className="grid gap-3">
          {items.map(s => (
            <Link
              to={`/cert-schemas/${s.id}`}
              key={s.id}
              className="block bg-ink-card border border-ink-border hover:border-brand-600 rounded-lg p-4 transition-colors"
            >
              <div className="flex items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-base">{s.label}</span>
                    <code className="text-xs text-ink-muted bg-ink-bg px-2 py-0.5 rounded border border-ink-border">{s.id}</code>
                    <span className="text-xs text-ink-muted">v{s.version}</span>
                  </div>
                  <div className="text-xs text-ink-muted">
                    {s.sections_count} sections · {s.fields_count} fields · {s.category || 'uncategorised'}
                    {s.trade && s.trade.length > 0 && (
                      <span> · {s.trade.join(', ')}</span>
                    )}
                  </div>
                </div>
                <div className="text-brand-600 self-center text-sm font-semibold">Edit →</div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Editor page ─────────────────────────────────────────────────────
export function CertSchemaEditorPage() {
  const { id } = useParams<{ id: string }>();
  const { push: toast } = useToast();
  const [schema, setSchema] = useState<CertSchema | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState<'structured' | 'json'>('structured');
  const [jsonText, setJsonText] = useState<string>('');
  const [jsonError, setJsonError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!id) return;
    try {
      const s = await api<CertSchema>('/admin/schemas/certificates/' + encodeURIComponent(id));
      setSchema(s);
      setJsonText(JSON.stringify(s, null, 2));
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [id]);

  useEffect(() => { void reload(); }, [reload]);

  function updateField(sectionIdx: number, fieldIdx: number, key: keyof SchemaField, value: unknown, isApplianceList = false) {
    if (!schema) return;
    const next: CertSchema = JSON.parse(JSON.stringify(schema));
    const sec = next.sections[sectionIdx];
    const arr = isApplianceList ? sec.fields_per_appliance : sec.fields;
    if (!arr) return;
    arr[fieldIdx] = { ...arr[fieldIdx], [key]: value };
    setSchema(next);
    setJsonText(JSON.stringify(next, null, 2));
  }

  function updateSectionLabel(sectionIdx: number, label: string) {
    if (!schema) return;
    const next: CertSchema = JSON.parse(JSON.stringify(schema));
    next.sections[sectionIdx].label = label;
    setSchema(next);
    setJsonText(JSON.stringify(next, null, 2));
  }

  // ── Form-builder mutations — add / remove sections and fields ──
  function mutate(fn: (s: CertSchema) => void) {
    if (!schema) return;
    const next: CertSchema = JSON.parse(JSON.stringify(schema));
    fn(next);
    setSchema(next);
    setJsonText(JSON.stringify(next, null, 2));
  }
  function nextId(prefix: string, taken: Set<string>): string {
    for (let i = 1; i < 999; i++) { const c = prefix + '_' + i; if (!taken.has(c)) return c; }
    return prefix + '_' + Date.now();
  }
  function addSection() {
    mutate((s) => {
      const id = nextId('section', new Set((s.sections || []).map((x) => x.id)));
      s.sections.push({ id, label: 'New Section', fields: [] });
    });
  }
  function removeSection(sIdx: number) {
    if (!window.confirm('Remove this whole section and all its fields?')) return;
    mutate((s) => { s.sections.splice(sIdx, 1); });
  }
  function addField(sIdx: number, isApplianceList: boolean) {
    mutate((s) => {
      const sec = s.sections[sIdx];
      const allIds = new Set([...(sec.fields || []), ...(sec.fields_per_appliance || [])].map((f) => f.id));
      const fld: SchemaField = { id: nextId('field', allIds), label: 'New field', type: 'text', required: false };
      if (isApplianceList) sec.fields_per_appliance = [...(sec.fields_per_appliance || []), fld];
      else sec.fields = [...(sec.fields || []), fld];
    });
  }
  function removeField(sIdx: number, fIdx: number, isApplianceList: boolean) {
    mutate((s) => {
      const sec = s.sections[sIdx];
      const arr = isApplianceList ? sec.fields_per_appliance : sec.fields;
      if (arr) arr.splice(fIdx, 1);
    });
  }

  async function save() {
    if (!schema || !id) return;
    let payload: CertSchema = schema;
    if (mode === 'json') {
      try {
        payload = JSON.parse(jsonText);
      } catch (e) {
        setJsonError('Invalid JSON: ' + (e as Error).message);
        return;
      }
      setJsonError(null);
    }
    setSaving(true);
    try {
      await api('/admin/schemas/certificates/' + encodeURIComponent(id), {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      toast('Saved — engineer form picks it up in seconds', 'success');
      await reload();
    } catch (e) {
      toast('Save failed: ' + (e as Error).message, 'error');
    } finally {
      setSaving(false);
    }
  }

  if (!id) return <div className="p-6">No schema id in URL</div>;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-start justify-between gap-6 mb-6">
        <div className="max-w-2xl">
          <Link to="/cert-schemas" className="text-brand-600 hover:underline text-sm">← Back to schemas</Link>
          <h1 className="text-2xl font-bold mt-1 mb-1">{schema?.label || id}</h1>
          <div className="text-ink-muted text-xs">
            <code>{id}</code> · v{schema?.version || '?'} · {(schema?.sections || []).length} sections
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <div className="flex bg-ink-card border border-ink-border rounded overflow-hidden text-sm">
            <button onClick={() => setMode('structured')}
                    className={`px-3 py-2 ${mode === 'structured' ? 'bg-brand-600 text-white' : 'text-ink-muted hover:text-ink'}`}>
              Structured
            </button>
            <button onClick={() => setMode('json')}
                    className={`px-3 py-2 ${mode === 'json' ? 'bg-brand-600 text-white' : 'text-ink-muted hover:text-ink'}`}>
              JSON
            </button>
          </div>
          <button onClick={save} disabled={saving}
                  className="bg-brand-600 hover:bg-brand-700 disabled:opacity-60 text-white px-5 py-2 rounded font-semibold text-sm">
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

      {error && <div className="bg-red-900/30 border border-red-700 text-red-200 p-3 rounded mb-4">{error}</div>}

      {schema === null ? (
        <div className="text-ink-muted">Loading…</div>
      ) : mode === 'json' ? (
        <div>
          {jsonError && <div className="bg-red-900/30 border border-red-700 text-red-200 p-3 rounded mb-3 text-sm font-mono">{jsonError}</div>}
          <textarea
            value={jsonText}
            onChange={e => { setJsonText(e.target.value); setJsonError(null); }}
            className="w-full h-[70vh] font-mono text-xs bg-ink-card border border-ink-border rounded p-4 focus:border-brand-600 outline-none"
            spellCheck={false}
          />
          <div className="text-ink-muted text-xs mt-2">
            Server validates the schema structure on save (id, version, sections, no duplicate field IDs).
            A backup of the current version is saved to <code>_history/</code> before any overwrite.
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {schema.sections.map((sec, sIdx) => {
            const isAppl = sec.type === 'appliance_list';
            const fields = isAppl ? sec.fields_per_appliance : sec.fields;
            return (
              <details key={sec.id} className="bg-ink-card border border-ink-border rounded-lg" open={sIdx < 2}>
                <summary className="cursor-pointer px-4 py-3 flex items-center gap-3">
                  {sec.letter && <span className="bg-brand-600 text-white font-bold rounded w-7 h-7 flex items-center justify-center text-sm shrink-0">{sec.letter}</span>}
                  <span className="font-semibold flex-1">{sec.label}</span>
                  {isAppl && <span className="text-xs bg-blue-900/40 text-blue-200 px-2 py-0.5 rounded border border-blue-700">per-appliance</span>}
                  <span className="text-xs text-ink-muted">{(fields || []).length} fields</span>
                </summary>
                <div className="px-4 pb-4 border-t border-ink-border">
                  <div className="mb-3 flex items-end gap-3">
                    <div className="flex-1">
                      <label className="text-xs font-semibold text-ink-muted uppercase tracking-wide">Section label</label>
                      <input
                        type="text"
                        value={sec.label}
                        onChange={e => updateSectionLabel(sIdx, e.target.value)}
                        className="mt-1 w-full bg-ink-bg border border-ink-border rounded px-3 py-2 text-sm focus:border-brand-600 outline-none"
                      />
                    </div>
                    <button onClick={() => removeSection(sIdx)}
                            className="shrink-0 text-xs text-red-300 hover:text-red-100 border border-red-800 hover:border-red-600 rounded px-3 py-2">
                      Remove section
                    </button>
                  </div>
                  {sec.help && (
                    <div className="text-xs text-ink-muted italic mb-3">{sec.help}</div>
                  )}
                  <div className="space-y-3 mt-2">
                    {(fields || []).map((f, fIdx) => (
                      <FieldEditor
                        key={fIdx}
                        field={f}
                        onUpdate={(key, value) => updateField(sIdx, fIdx, key, value, isAppl)}
                        onRemove={() => removeField(sIdx, fIdx, isAppl)}
                      />
                    ))}
                    {(fields || []).length === 0 && (
                      <div className="text-xs text-ink-muted italic">No fields yet — add the first one below.</div>
                    )}
                  </div>
                  <button onClick={() => addField(sIdx, isAppl)}
                          className="mt-3 text-sm bg-ink-bg border border-ink-border hover:border-brand-600 text-ink rounded px-3 py-1.5">
                    + Add field
                  </button>
                </div>
              </details>
            );
          })}
          <button onClick={addSection}
                  className="w-full bg-ink-card border border-dashed border-ink-border hover:border-brand-600 text-ink-muted hover:text-ink rounded-lg py-3 text-sm font-semibold">
            + Add section
          </button>
        </div>
      )}
    </div>
  );
}

// ── Per-field editor ────────────────────────────────────────────────
const FIELD_TYPES = ['text', 'textarea', 'number_text', 'date', 'select', 'toggle', 'safe_toggle', 'signature', 'heading'];

function FieldEditor({ field, onUpdate, onRemove }: {
  field: SchemaField;
  onUpdate: (key: keyof SchemaField, value: unknown) => void;
  onRemove: () => void;
}) {
  const optionsText = useMemo(() => {
    return Array.isArray(field.options) ? field.options.join(', ') : '';
  }, [field.options]);
  const needsOptions = ['select', 'toggle', 'safe_toggle'].includes(String(field.type));

  return (
    <div className="border border-ink-border rounded p-3 bg-ink-bg">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <input
          type="text"
          value={String(field.id || '')}
          onChange={e => onUpdate('id', e.target.value)}
          placeholder="field_id"
          className="text-xs font-mono text-brand-600 bg-ink-card px-2 py-1 rounded border border-ink-border w-44 focus:border-brand-600 outline-none"
        />
        <select
          value={String(field.type || 'text')}
          onChange={e => onUpdate('type', e.target.value)}
          className="text-xs bg-ink-card text-ink-muted px-2 py-1 rounded border border-ink-border focus:border-brand-600 outline-none"
        >
          {FIELD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        {field.required && <span className="text-xs bg-red-900/40 text-red-200 px-2 py-0.5 rounded border border-red-700">required</span>}
        {field.customer_visible === false && <span className="text-xs bg-yellow-900/30 text-yellow-200 px-2 py-0.5 rounded border border-yellow-700">internal only</span>}
        <button onClick={onRemove} className="ml-auto text-xs text-red-300 hover:text-red-100">Remove field</button>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold text-ink-muted uppercase tracking-wide">Label</label>
          <input
            type="text"
            value={String(field.label || '')}
            onChange={e => onUpdate('label', e.target.value)}
            className="mt-1 w-full bg-ink-card border border-ink-border rounded px-2 py-1.5 text-sm focus:border-brand-600 outline-none"
          />
        </div>
        <div>
          <label className="text-xs font-semibold text-ink-muted uppercase tracking-wide">Voice prompt</label>
          <input
            type="text"
            value={String(field.voice_prompt || '')}
            placeholder="(blank to disable voice fill on this field)"
            onChange={e => onUpdate('voice_prompt', e.target.value || null)}
            className="mt-1 w-full bg-ink-card border border-ink-border rounded px-2 py-1.5 text-sm focus:border-brand-600 outline-none"
          />
        </div>
        {needsOptions && (
          <div className="md:col-span-2">
            <label className="text-xs font-semibold text-ink-muted uppercase tracking-wide">Options (comma-separated)</label>
            <input
              type="text"
              value={optionsText}
              placeholder="e.g. Pass, Fail, N/A"
              onChange={e => onUpdate('options', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
              className="mt-1 w-full bg-ink-card border border-ink-border rounded px-2 py-1.5 text-sm font-mono focus:border-brand-600 outline-none"
            />
          </div>
        )}
        <div className="flex items-center gap-4 md:col-span-2">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={field.required === true}
              onChange={e => onUpdate('required', e.target.checked)}
              className="rounded"
            />
            Required
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={field.customer_visible !== false}
              onChange={e => onUpdate('customer_visible', e.target.checked)}
              className="rounded"
            />
            Customer-visible
          </label>
        </div>
      </div>
    </div>
  );
}
